/**
 * The one way to withhold a dancer's opted-out field on an egress path.
 *
 * Nine leaks of the same class shipped between 2026-07-28 and 2026-07-30, each
 * one a surface that read a gated column and forgot to consult the toggle. The
 * two `/dancers/[slug]` loaders eventually grew a local `m()` / `s()` pair; this
 * module is that pattern, named, shared, and keyed so the egress detector can
 * see it.
 *
 * Usage — a loader that already reads the dancer's `professional_profiles` row
 * adds the four toggle columns to its existing projection and builds gates from
 * it, at no extra query cost:
 *
 *     const gates = dancerFieldGates(row, { unrestricted: isSelf || isAdmin });
 *     return {
 *       bio: gates.bio_public(row.bio),
 *       height_cm: gates.measurements_public(dp?.height_cm),
 *       instagram_handle: gates.social_public(row.instagram_handle),
 *       phone: gates.contact_public(profile.phone_number),
 *     };
 *
 * The returned object is keyed by TOGGLE NAME, not by a short alias. That is
 * deliberate: it puts the toggle on the emission line, so a reviewer (and the
 * detector) can tell `gates.measurements_public(height)` from the mistake of
 * `gates.social_public(height)`.
 *
 * These gates are an application layer ABOVE row-level security, not a
 * replacement for it. RLS decides which rows a caller may read; these decide
 * which fields of an already-readable row may be shown.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import type { Database } from '@kit/supabase/database';

import {
  toggleForColumn,
  type DancerVisibilityPreferences,
} from './view-fields';

/** Passes the value through, or returns null when the dancer opted out. */
export type FieldGate = <T>(value: T | null | undefined) => T | null;

export type DancerFieldGates = Record<
  keyof DancerVisibilityPreferences,
  FieldGate
> & {
  /**
   * The caller is the dancer or an admin.
   *
   * Exposed for the handful of fields that no toggle covers but that still
   * must not reach an arbitrary caller — `profiles.date_of_birth` above all,
   * which the public dancer surface withholds unconditionally and replaces
   * with completed years.
   */
  readonly unrestricted: boolean;
  /**
   * Whether a toggle lets its section through, as a boolean.
   *
   * For callers that decide whether to render a whole section (the resume
   * block, the socials row, the casting-contact rail) rather than withhold a
   * single value. Same defaults and the same `unrestricted` bypass as the
   * gates, so no caller has to restate `?? true` / `?? false` by hand — that
   * restatement is exactly how a surface drifts from the registry (BAL-852).
   */
  readonly allows: (toggle: keyof DancerVisibilityPreferences) => boolean;
};

/**
 * Column defaults, mirroring `professional_profiles`. A legacy row with NULL
 * toggles renders its bio, socials and resume, and withholds measurements and
 * contact details. Duplicated from `view-fields.ts`'s private
 * `PREFERENCE_DEFAULTS` rather than exported from it, because a gate must not
 * silently change meaning if that constant is ever relaxed for the registry.
 */
const DEFAULTS: Record<keyof DancerVisibilityPreferences, boolean> = {
  bio_public: true,
  social_public: true,
  resume_public: true,
  measurements_public: false,
  contact_public: false,
};

const TOGGLES = Object.keys(DEFAULTS) as (keyof DancerVisibilityPreferences)[];

/** The four toggle columns, for adding to an existing SELECT projection. */
export const VISIBILITY_COLUMNS =
  'bio_public, measurements_public, contact_public, social_public, resume_public';

export interface DancerFieldGateOptions {
  /**
   * The caller may see everything: they ARE the dancer, or they hold an admin
   * role. Mirrors the `ctx.viewer === 'admin'` bypass in `isFieldVisible`.
   * Anything softer than self-or-admin belongs in the registry's role rules,
   * not here.
   */
  unrestricted?: boolean;
}

/**
 * Build gates from a row that already carries the toggle columns.
 */
export function dancerFieldGates(
  prefs: DancerVisibilityPreferences | null | undefined,
  options: DancerFieldGateOptions = {},
): DancerFieldGates {
  const unrestricted = options.unrestricted === true;
  const allows = (toggle: keyof DancerVisibilityPreferences): boolean =>
    unrestricted || (prefs?.[toggle] ?? DEFAULTS[toggle]);

  return {
    ...(Object.fromEntries(
      TOGGLES.map((toggle) => {
        const allowed = allows(toggle);
        const gate: FieldGate = (value) => (allowed ? (value ?? null) : null);
        return [toggle, gate];
      }),
    ) as Record<keyof DancerVisibilityPreferences, FieldGate>),
    unrestricted,
    allows,
  };
}

/**
 * Apply gates to every gated column of a whole row, in place of listing the
 * columns by hand.
 *
 * For emissions that hand back a row fetched with `select('*')`. Those are the
 * worst case of this bug class: the wildcard means the emission does not name
 * the leaking columns, so neither a reviewer nor a key-based detector can see
 * them, and a column added by a later migration leaks the day it lands.
 * Resolving each key through `toggleForColumn` keeps the registry as the single
 * authority (R4) and covers columns that do not exist yet.
 *
 * Ungated columns pass through untouched.
 */
export function applyDancerFieldGates<T extends Record<string, unknown>>(
  row: T | null | undefined,
  gates: DancerFieldGates,
): T | null {
  if (!row) return null;
  return Object.fromEntries(
    Object.entries(row).map(([column, value]) => {
      const toggle = toggleForColumn(column);
      return [column, toggle ? gates[toggle](value) : value];
    }),
  ) as T;
}

/** Every field withheld. Used when the dancer's row could not be read. */
export function closedDancerFieldGates(): DancerFieldGates {
  return dancerFieldGates({
    bio_public: false,
    social_public: false,
    resume_public: false,
    measurements_public: false,
    contact_public: false,
  });
}

type Client = SupabaseClient<Database>;

/**
 * Fetch one dancer's toggles and build gates.
 *
 * Prefer adding `VISIBILITY_COLUMNS` to a projection you already run. Use this
 * only where no `professional_profiles` read exists for the dancer. For a list,
 * use `fetchDancerFieldGatesBatch` — never call this per row.
 */
export async function fetchDancerFieldGates(
  client: Client,
  dancerUserId: string,
  options: DancerFieldGateOptions = {},
): Promise<DancerFieldGates> {
  if (options.unrestricted) return dancerFieldGates(null, options);

  const { data } = await client
    .from('professional_profiles')
    .select(VISIBILITY_COLUMNS)
    .eq('user_id', dancerUserId)
    .eq('profile_type', 'dancer')
    .maybeSingle();

  // No row means the caller could not read the dancer's preferences, which is
  // not a licence to show everything.
  if (!data) return closedDancerFieldGates();

  return dancerFieldGates(data, options);
}

/**
 * Fetch toggles for many dancers in ONE query and return per-dancer gates.
 * A dancer missing from the result gets closed gates.
 */
export async function fetchDancerFieldGatesBatch(
  client: Client,
  dancerUserIds: string[],
  options: DancerFieldGateOptions = {},
): Promise<Map<string, DancerFieldGates>> {
  const unique = [...new Set(dancerUserIds)].filter(Boolean);
  const gates = new Map<string, DancerFieldGates>();
  if (unique.length === 0) return gates;

  if (options.unrestricted) {
    const open = dancerFieldGates(null, options);
    for (const id of unique) gates.set(id, open);
    return gates;
  }

  const { data } = await client
    .from('professional_profiles')
    .select(`user_id, ${VISIBILITY_COLUMNS}`)
    .in('user_id', unique)
    .eq('profile_type', 'dancer');

  for (const row of data ?? []) {
    if (row.user_id) gates.set(row.user_id, dancerFieldGates(row, options));
  }
  for (const id of unique) {
    if (!gates.has(id)) gates.set(id, closedDancerFieldGates());
  }

  return gates;
}
